import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a sibling product picker.
 * This allows users to switch between related products (color variants).
 * Reuses patterns from VariantPicker for consistency and efficiency.
 *
 * @extends Component
 */
export default class SiblingPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.siblingClicked.bind(this));

    this.addEventListener('change', this.siblingChanged.bind(this));
  }

  /**
   * Handles the sibling click event.
   * @param {Event} event - The sibling click event.
   */
  siblingClicked(event) {
    if (!(event.target instanceof Element)) return;
    
    const link = event.target.closest('.sibling-option__swatch');
    if (!link || !(link instanceof HTMLElement)) return;

    // Only handle image swatch clicks, not radio buttons
    if (event.target.closest('input[type="radio"]')) return;

    const newProductUrl = link.dataset.productUrl;
    const newProductId = link.dataset.productId;

    if (!newProductUrl || !newProductId) return;

    // Determine if we should intercept the click for enhanced navigation
    const isOnProductPage = this.dataset.templateProductMatch === 'true' && 
                           !this.closest('product-card') && 
                           !this.closest('quick-add-dialog');
    
    const shouldMorphMain = isOnProductPage;
    const shouldInterceptClick = shouldMorphMain || this.closest('product-card');

    if (shouldInterceptClick) {
      event.preventDefault();
      
      // Build the request URL with current variant options
      const requestUrl = this.buildRequestUrlWithVariantOptions(newProductUrl);
      
      // Fetch and update content
      this.fetchUpdatedSection(requestUrl, shouldMorphMain, this.buildNavigationUrl(newProductUrl));
    }
    // If not intercepting, let the default link behavior handle navigation
  }
  /**
   * Handles radio button change events for non-image siblings.
   * @param {Event} event - The change event.
   */
  siblingChanged(event) {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    const newProductUrl = event.target.dataset.productUrl;
    const newProductId = event.target.dataset.productId;

    if (!newProductUrl || !newProductId) return;

    // Determine if we should intercept for enhanced navigation
    const isOnProductPage = this.dataset.templateProductMatch === 'true' && 
                           !this.closest('product-card') && 
                           !this.closest('quick-add-dialog');
    
    const shouldMorphMain = isOnProductPage;
    const shouldInterceptClick = shouldMorphMain || this.closest('product-card');

    if (shouldInterceptClick) {
      event.preventDefault();
      
      // Build the request URL with current variant options
      const requestUrl = this.buildRequestUrlWithVariantOptions(newProductUrl);
      
      // Fetch and update content
      this.fetchUpdatedSection(requestUrl, shouldMorphMain, this.buildNavigationUrl(newProductUrl));
    } else {
      // Navigate to the new product URL with variant options
      window.location.href = this.buildNavigationUrl(newProductUrl);
    }
  }

  /**
   * Gets the current variant options from the variant picker.
   * @returns {Object} Object containing option1, option2, option3 values for non-color options.
   */
  getCurrentVariantOptions() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return {};

    /** @type {Record<string, string>} */
    const options = {};
    
    // Get all fieldsets in the variant picker
    const fieldsets = variantPicker.querySelectorAll('fieldset[data-option-id]');
    
    fieldsets.forEach((fieldset, index) => {
      // Get the option name from the legend
      const legend = fieldset.querySelector('legend');
      const optionName = legend ? legend.textContent?.trim() : '';
      
      // Get the selected input in this fieldset
      const selectedInput = fieldset.querySelector('input:checked');
      
      if (selectedInput instanceof HTMLInputElement && optionName) {
        const optionValue = selectedInput.value;
        
        // Skip color-related options since we're changing products (colors)
        if (optionValue && !this.isColorOption(optionName)) {
          // Map to option1, option2, option3 based on fieldset order
          const optionKey = `option${index + 1}`;
          options[optionKey] = optionValue;
        }
      }
    });
    
    return options;
  }

  /**
   * Checks if an option name is color-related.
   * @param {string} optionName - The option name to check.
   * @returns {boolean} True if the option is color-related.
   */
  isColorOption(optionName) {
    const colorKeywords = ['color', 'colour', 'shade', 'pattern'];
    const lowerName = optionName.toLowerCase();
    return colorKeywords.some(keyword => lowerName.includes(keyword));
  }

  /**
   * Builds the request URL with current variant options applied.
   * @param {string} productUrl - The new product URL.
   * @returns {string} The request URL with variant options.
   */
  buildRequestUrlWithVariantOptions(productUrl) {
    this.#pendingRequestUrl = productUrl;
    
    const currentOptions = this.getCurrentVariantOptions();
    /** @type {string[]} */
    const params = [];

    // Add variant options as URL parameters (option1, option2, option3)
    Object.entries(currentOptions).forEach(([optionKey, optionValue]) => {
      params.push(`${optionKey}=${encodeURIComponent(optionValue)}`);
    });

    // For product cards, use section rendering
    if (this.closest('product-card')) {
      const cleanUrl = productUrl.includes('?') ? productUrl.split('?')[0] : productUrl;
      const paramString = params.length > 0 ? `&${params.join('&')}` : '';
      return `${cleanUrl}?section_id=section-rendering-product-card${paramString}`;
    }

    // For product pages, add variant options to the URL
    const cleanUrl = productUrl.includes('?') ? productUrl.split('?')[0] : productUrl;
    const paramString = params.length > 0 ? `?${params.join('&')}` : '';
    return `${cleanUrl}${paramString}`;
  }

  /**
   * Builds the navigation URL for browser history.
   * @param {string} productUrl - The new product URL.
   * @returns {string} The URL for browser navigation.
   */
  buildNavigationUrl(productUrl) {
    const currentOptions = this.getCurrentVariantOptions();
    
    if (Object.keys(currentOptions).length === 0) {
      return productUrl;
    }

    const url = new URL(productUrl, window.location.origin);
    
    // Add variant options as URL parameters
    Object.entries(currentOptions).forEach(([optionName, optionValue]) => {
      url.searchParams.set(optionName, optionValue);
    });

    return url.toString();
  }


  /**
   * Builds the request URL for sibling product switching.
   * Similar to VariantPicker's buildRequestUrl but simplified for product switching.
   * @param {string} productUrl - The new product URL.
   * @returns {string} The request URL.
   */
  buildRequestUrl(productUrl) {
    // Store pending URL for abort logic, similar to variant picker
    this.#pendingRequestUrl = productUrl;

    // For product cards, use section rendering
    if (this.closest('product-card')) {
      const cleanUrl = productUrl.includes('?') ? productUrl.split('?')[0] : productUrl;
      return `${cleanUrl}?section_id=section-rendering-product-card`;
    }

    // For product pages, fetch the full page
    return productUrl;
  }

  /**
   * Fetches the updated section.
   * Adapted from VariantPicker's fetchUpdatedSection.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   * @param {string} newProductUrl - The new product URL for history update.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, newProductUrl = '') {
    // Abort previous request if still pending
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Add loading state
    this.classList.add('sibling-picker--loading');

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (shouldMorphMain) {
          this.updateMain(html, newProductUrl);
        } else {
          this.updateProductCard(html);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error('Error loading sibling product:', error);
        }
      })
      .finally(() => {
        this.classList.remove('sibling-picker--loading');
        this.#abortController = undefined;
      });
  }

  /**
   * Re-renders the entire main content.
   * Adapted from VariantPicker's updateMain method.
   * @param {Document} newHtml - The new HTML.
   * @param {string} newProductUrl - The new product URL for history update.
   */
  updateMain(newHtml, newProductUrl) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No main content found');
    }

    // Update URL using requestYieldCallback like variant picker
    requestYieldCallback(() => {
      history.pushState({}, '', newProductUrl);
    });

    // Morph the main content
    morph(main, newMain);

    // After morphing, try to select the matching variant based on current options
    this.selectMatchingVariant(new URL(newProductUrl).searchParams);
  }

  /**
   * Attempts to select the matching variant after product switch.
   * @param {URLSearchParams} searchParams - The search parameters from the URL
   */
  selectMatchingVariant(searchParams) {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    // Handle option1, option2, option3 from URL parameters
    ['option1', 'option2', 'option3'].forEach(optionKey => {
      const optionValue = searchParams.get(optionKey);
      if (!optionValue) return;

      // Find the input with the matching value
      const optionInput = variantPicker.querySelector(`input[value="${optionValue}"]`);
      
      if (optionInput instanceof HTMLInputElement) {
        // Check if this option is color-related by checking the fieldset legend
        const fieldset = optionInput.closest('fieldset');
        const legend = fieldset?.querySelector('legend');
        const optionName = legend?.textContent?.trim();
        
        // Only select non-color options
        if (optionName && !this.isColorOption(optionName)) {
          optionInput.checked = true;
          optionInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /**
   * Updates a product card with the new product data.
   * Simplified version that preserves the sibling picker.
   * @param {Document} newHtml - The new HTML document.
   */
  updateProductCard(newHtml) {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    const newProductCard = newHtml.querySelector('product-card');
    if (!newProductCard) return;

    // Update product card data attributes
    if (productCard instanceof HTMLElement && newProductCard instanceof HTMLElement) {
      productCard.dataset.productId = newProductCard.dataset.productId || '';
    }

    // Update the product card link
    const currentLink = productCard.querySelector('.product-card__link, [ref="productCardLink"]');
    const newLink = newProductCard.querySelector('.product-card__link, [ref="productCardLink"]');
    if (currentLink instanceof HTMLAnchorElement && newLink instanceof HTMLAnchorElement) {
      currentLink.href = newLink.href;
    }

    // Morph the product card content while preserving the sibling picker
    const currentContent = productCard.querySelector('.product-card__content');
    const newContent = newProductCard.querySelector('.product-card__content');

    if (currentContent && newContent) {
      // Preserve the current sibling picker
      const currentSiblingPicker = currentContent.querySelector('sibling-picker');

      // Morph the content
      morph(currentContent, newContent);

      // Re-insert the sibling picker if it existed
      if (currentSiblingPicker) {
        const insertionPoint = currentContent.querySelector('[data-sibling-picker-slot]') ||
                             currentContent.querySelector('.product-card__title') ||
                             currentContent.firstElementChild;

        if (insertionPoint && insertionPoint.parentNode) {
          insertionPoint.parentNode.insertBefore(currentSiblingPicker, insertionPoint.nextSibling);
        }
      }
    }
  }
}

if (!customElements.get('sibling-picker')) {
  customElements.define('sibling-picker', SiblingPicker);
}
